import React, { useState } from 'react';
import { Formik } from 'formik-v2';
import * as Yup from 'yup';
import { ActionButton } from '@ax2/common/components/ActionButton';
import formStyles from '../styles/components/form/Forms.module.css';
import base from '../styles/Base.module.css';
import DefaultPage from './DefaultPage';
import TextField from '../components/form/TextField';
import { useUserContext } from '../contexts/UserContext';
import { requestDemo } from '../rest/demoRequestClient';
import DemoRequest from '../model/DemoRequest';
import _ from 'lodash';

interface RequestDemoFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
}

const requestDemoSchema = Yup.object<RequestDemoFormValues>().shape({
  firstName: Yup.string()
    .trim()
    .min(1, 'Too short')
    .max(50, 'Too long')
    .required('First name is required'),
  lastName: Yup.string()
    .trim()
    .min(1, 'Too short')
    .max(50, 'Too long')
    .required('Last name is required'),
  email: Yup.string()
    .trim()
    .email('Must be a valid email')
    .required('Email is required'),
  phone: Yup.string()
    .trim()
    .test(
      'phoneValid',
      'Invalid number format, please provide a full number',
      async value => {
        const lib = await import('libphonenumber-js');
        return value ? lib.isValidNumber(value, 'US') : true;
      }
    ),
  role: Yup.string().trim(),
});

export interface RequestDemoFromProps {
  onComplete?: () => void;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

export const RequestDemoForm: React.FC<RequestDemoFromProps> = ({
  onComplete,
  firstName,
  lastName,
  email,
  role,
}) => {
  const [formError, setFormError] = useState<string>();
  const initialValues: RequestDemoFormValues = {
    firstName: firstName || '',
    lastName: lastName || '',
    email: email || '',
    phone: '',
    role: role || '',
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={requestDemoSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        setFormError(undefined);
        try {
          await requestDemo(
            new DemoRequest(
              _.trim(values.firstName),
              _.trim(values.lastName),
              _.trim(values.email),
              _.isEmpty(_.trim(values.phone))
                ? undefined
                : _.trim(values.phone),
              _.isEmpty(_.trim(values.role)) ? undefined : _.trim(values.role)
            )
          );
          setSubmitting(false);
          onComplete && onComplete();
        } catch (e) {
          setSubmitting(false);
          setFormError(e.description || e.message || e.toString());
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit} className={formStyles.form}>
          <div className={formStyles.formItems}>
            <div
              className={[
                formStyles.formRow,
                formStyles.formItemFullWidth,
              ].join(' ')}
            >
              <TextField
                name="firstName"
                label="First Name"
                labelNote="required"
                wrapperClassName={formStyles.formRowCell}
              />
              <TextField
                name="lastName"
                label="Last Name"
                labelNote="required"
                wrapperClassName={formStyles.formRowCell}
              />
            </div>
            <TextField
              name="email"
              label="Company Email"
              labelNote="required"
              wrapperClassName={formStyles.formItemFullWidth}
            />
            <TextField
              name="phone"
              label="Phone"
              labelNote="optional"
              wrapperClassName={formStyles.formItemFullWidth}
            />
            <TextField
              name="role"
              label="Your Role"
              labelNote="optional"
              wrapperClassName={formStyles.formItemFullWidth}
            />
            {formError ? (
              <div className={formStyles.formError}>{formError}</div>
            ) : null}
            <div className={formStyles.formButtons}>
              <ActionButton
                onClick={() => !isSubmitting && handleSubmit()}
                loading={isSubmitting}
                type="primary"
                text="Submit"
              />
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

const RequestDemo: React.FC = () => {
  const [complete, setComplete] = useState(false);
  const user = useUserContext();
  return (
    <DefaultPage header={{ title: 'Request a Demo' }}>
      <div className={[base.actionBox, base.actionBoxFullWidth].join(' ')}>
        {complete ? (
          <p className={base.sectionText}>
            Thank you for your inquiry.<br></br>
            We will contact you within 24 hours to set up your demo.
          </p>
        ) : (
          <RequestDemoForm
            onComplete={() => setComplete(true)}
            firstName={(user && user.firstName) || ''}
            lastName={(user && user.lastName) || ''}
            email={(user && user.email) || ''}
            role={(user && user.companyRole) || ''}
          />
        )}
      </div>
    </DefaultPage>
  );
};

export default RequestDemo;
